import { useEffect, useState, useCallback, Fragment } from "react"
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  flexRender,
  Row,
  PaginationState
} from "@tanstack/react-table"
import { TanstackPagination } from "../pagination"
import ChartPagination from "../chart-pagination"
import ThInfoCell from "./cells/th-info-cell"
import {
  Sorter,
  DropdownFilter,
  ActiveDeletedFilter,
  SearchBar
} from "./toolbar"
import { classNames } from "../../utils"
import If from "../if"
import { SortDirection, BBColumnDef, ViewType } from "../../../types"
import { ListSkeleton } from "../skeletons"
import { rowStatusFilter } from "./helpers"

type PaginationType = "default" | "chart"

type Props<T> = {
  data: T[]
  viewType: ViewType
  canChangeType?: boolean
  columns: BBColumnDef<T>[]
  dropdownFilterColumnId?: string
  searchBarVisible?: boolean
  searchPlaceholder?: string
  pageSize?: number
  gridsPerPage?: number
  pageCount?: number
  paginationType?: PaginationType
  pagination?: PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>
  renderCard?: (row: Row<T>) => JSX.Element | null
  extraHeaderElement?: React.ReactNode
  extraHeaderElementClassName?: string
  extraElementBeforeTable?: React.ReactNode
  extraElementAfterTable?: React.ReactNode
  noDataElement?: React.ReactNode
  shrinkFilterSort?: boolean
  noHeader?: boolean
  noSorter?: boolean
  gridClass?: string
  isLoading?: boolean
  loaderSkeleton?: React.ReactNode
  defaultSortBy?: string
  defaultSortDir?: SortDirection
  className?: string
  tableContainerClassName?: string
  tableClassName?: string
  tableHeaderClassName?: string
  searchClassName?: string
  containColumnsSorter?: boolean
  removeMobilePagination?: boolean
  hasActiveDeletedFilter?: boolean
  autoResetPageIndex?: boolean
  hasColumnFilter?: boolean // Filter rows by column ID (not quit useful)
  customFilterFns?: Record<string, any> // Custom filter functions
  customFilterComponent?: React.ReactNode // Custom filter component
  columnFilters?: ColumnFiltersState // External column filters state
  setColumnFilters?: (
    filters:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState)
  ) => void // External column filters setter
  onSearchChange?: (value: string) => void // Server-side search callback
  rowCount?: number // Total server-side row count for pagination display
  dividedRows?: boolean
  getRowProps?: (row: Row<T>) => {
    onClick?: () => void
    className?: string
  }
}

const Table = <P extends unknown>({
  data,
  viewType,
  columns,
  searchPlaceholder,
  dropdownFilterColumnId,
  searchBarVisible = true,
  pageSize = 10,
  gridsPerPage = 12,
  pageCount,
  paginationType = "default",
  pagination,
  setPagination,
  renderCard,
  extraHeaderElement = null,
  extraHeaderElementClassName = "ml-auto",
  extraElementBeforeTable = null,
  extraElementAfterTable = null,
  noDataElement = null,
  shrinkFilterSort = false,
  noHeader = false,
  noSorter = false,
  gridClass,
  isLoading = false,
  loaderSkeleton = <ListSkeleton />,
  defaultSortBy,
  defaultSortDir,
  className = "",
  tableContainerClassName = "",
  tableClassName = "table-fixed",
  tableHeaderClassName,
  searchClassName,
  containColumnsSorter = false,
  removeMobilePagination = false,
  autoResetPageIndex = true,
  hasActiveDeletedFilter = false,
  hasColumnFilter = true,
  customFilterFns = {},
  customFilterComponent = null,
  columnFilters: externalColumnFilters,
  setColumnFilters: externalSetColumnFilters,
  onSearchChange,
  rowCount,
  dividedRows = true,
  getRowProps
}: Props<P>) => {
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([])
  const columnFilters = externalColumnFilters ?? internalColumnFilters
  const setColumnFilters = externalSetColumnFilters ?? setInternalColumnFilters
  const [globalFilter, setGlobalFilter] = useState("")
  const instance = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter: onSearchChange ? "" : globalFilter,
      ...(pagination ? { pagination } : {})
    },
    ...(pageCount ? { pageCount } : {}),
    ...(setPagination ? { onPaginationChange: setPagination } : {}),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: pagination ? false : autoResetPageIndex,
    manualPagination: !!pagination,
    filterFns: { rowStatusFilter, ...customFilterFns },
    initialState:
      containColumnsSorter && defaultSortBy && defaultSortDir
        ? { sorting: [{ id: defaultSortBy, desc: defaultSortDir === "desc" }] }
        : undefined
  })

  useEffect(() => {
    instance.setPageSize(viewType === "table" ? pageSize : gridsPerPage)
  }, [viewType, instance, pageSize, gridsPerPage])

  const onSearch = useCallback(
    (value: string | number) => {
      setGlobalFilter(String(value))
      onSearchChange?.(String(value))
    },
    [onSearchChange]
  )

  const { rows } = instance.getRowModel()

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="-mx-6">
        <div
          className={`inline-block min-w-full w-full align-middle border-white ${
            viewType === "grid" ? "bg-slate-100" : ""
          }`}
        >
          {searchBarVisible && (
            <>
              <SearchBar
                id="table-search-input"
                className={`pb-4 px-6 lg:px-6 ${
                  extraHeaderElement ? "xl:flex" : "flex"
                }`}
                searchClassName={searchClassName}
                globalFilter={globalFilter}
                onSearch={onSearch}
                searchPlaceholder={searchPlaceholder ?? ""}
              >
                <div
                  className={`w-full flex items-center justify-between ${
                    extraHeaderElement ? "mt-4 xl:mt-0" : ""
                  }`}
                >
                  {customFilterComponent ? (
                    customFilterComponent
                  ) : (
                    <>
                      {hasColumnFilter && dropdownFilterColumnId && (
                        <DropdownFilter
                          columnID={dropdownFilterColumnId}
                          instance={instance as any}
                          className="flex items-center"
                          shrinkFilter={shrinkFilterSort}
                          popoverPlace="left"
                          popoverPlaceInMobile={
                            extraHeaderElement ? "left" : "right"
                          }
                        />
                      )}

                      {hasActiveDeletedFilter && dropdownFilterColumnId && (
                        <ActiveDeletedFilter
                          className="flex items-center"
                          columnID={dropdownFilterColumnId}
                          shrinkFilter={shrinkFilterSort}
                          columnFilters={columnFilters}
                          setColumnFilters={setColumnFilters}
                        />
                      )}
                    </>
                  )}
                  {!noSorter && (
                    <Sorter
                      headerGroups={instance.getHeaderGroups()}
                      className="ml-1 lg:ml-3 flex items-center"
                      shrinkSort={shrinkFilterSort}
                      defaultSortBy={defaultSortBy}
                      defaultSortDir={defaultSortDir}
                      popoverPlace="left"
                      popoverPlaceInMobile={
                        extraHeaderElement ? "left" : "right"
                      }
                    />
                  )}
                  <div className={extraHeaderElementClassName}>
                    {extraHeaderElement}
                  </div>
                </div>
              </SearchBar>
              {!extraElementBeforeTable && <div className="h-2" />}
            </>
          )}
          {extraElementBeforeTable}
          <If
            condition={isLoading}
            then={loaderSkeleton}
            else={
              <div
                className={`px-6 w-full overflow-auto ${tableContainerClassName}`}
              >
                {viewType === "table" ? (
                  <table className={`w-full max-w-full ${tableClassName}`}>
                    <thead className={noHeader ? "sr-only" : ""}>
                      {instance.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id} className="text-neutral-400">
                          {headerGroup.headers.map((header, i) => {
                            const foundCol = columns.find(
                              c => c.id === header.id
                            )
                            const hasColumnSort =
                              containColumnsSorter && header.column.getCanSort()
                            return (
                              <th
                                scope="col"
                                key={header.id}
                                colSpan={foundCol?.colSpan || header.colSpan}
                                className={classNames(
                                  tableHeaderClassName ||
                                    (i === 0
                                      ? "py-4 pl-2 pr-3 text-left"
                                      : "py-4 px-3 text-left text-sm font-medium uppercase"),
                                  header.column.columnDef?.meta?.headerClassName
                                )}
                              >
                                {header.isPlaceholder ? null : (
                                  <ThInfoCell
                                    hasColumnSort={hasColumnSort}
                                    header={header}
                                  />
                                )}
                              </th>
                            )
                          })}
                        </tr>
                      ))}
                    </thead>
                    {rows.length ? (
                      <tbody
                        className={
                          dividedRows ? "divide-y divide-gray-200" : ""
                        }
                      >
                        {rows.map(row => {
                          const { onClick, className: rowClassName } =
                            getRowProps?.(row) ?? {}
                          return (
                            <tr
                              key={row.id}
                              className={`group hover:bg-gray-100 ${
                                rowClassName ?? ""
                              }`}
                              onClick={onClick}
                            >
                              {row.getVisibleCells().map(cell => (
                                <Fragment key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </Fragment>
                              ))}
                            </tr>
                          )
                        })}
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          {noDataElement || (
                            <td colSpan={columns.length}>
                              <div className="px-2 py-4 text-neutral-500 text-lg">
                                No rows found
                              </div>
                            </td>
                          )}
                        </tr>
                      </tbody>
                    )}
                  </table>
                ) : rows.length ? (
                  <ul
                    className={
                      gridClass ??
                      "grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 py-4"
                    }
                  >
                    {rows.map(row => (
                      <li
                        key={row.id}
                        className="col-span-1 flex flex-col text-center bg-white rounded-lg shadow divide-y divide-gray-200 overflow-hidden"
                      >
                        {renderCard?.(row)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-2 py-4 text-neutral-500">
                    No rows found
                  </div>
                )}
              </div>
            }
          />
          {extraElementAfterTable}
          {instance.getPageCount() > 1 ? (
            <div className="px-6">
              {paginationType === "default" && (
                <TanstackPagination
                  instance={instance as any}
                  totalCount={instance.getPageCount()}
                  viewType={viewType}
                  removeMobilePagination={removeMobilePagination}
                  serverRowCount={rowCount}
                />
              )}
              {paginationType === "chart" && (
                <ChartPagination
                  instance={instance as any}
                  totalCount={instance.getPageCount()}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Table
